import { z } from 'zod'

/**
 * O contrato do modo historia. docs/21-modo-historia.md.
 *
 * Mora aqui porque tres processos leem as mesmas coisas: o `api` (acesso, ultimo tile), o `world-server`
 * (movimento, desafio) e o cliente do mundo (`web-history-mode`). Uma constante copiada em dois lugares e
 * uma regra que diverge no primeiro ajuste.
 *
 * As constantes NAO moram em `packages/engine/src/constants.ts`: nao sao regra de duelo, e o motor nao pode
 * saber que o mundo existe (docs/21 secao 11).
 */

/** docs/21 secao 11. */
export const STORY_ROOM_CAPACITY = 50
export const STORY_STEPS_PER_SECOND = 6
export const STORY_PATCH_RATE_MS = 100
export const STORY_SESSION_TTL_MS = 60_000
export const STORY_POSITION_REPORT_MS = 15_000
export const STORY_CHALLENGE_RANGE_TILES = 3
export const STORY_CHALLENGE_TTL_MS = 20_000
export const STORY_CHALLENGE_INTERVAL_MS = 5_000
export const STORY_EMOTE_INTERVAL_MS = 2_000
/** O mapa de entrada: onde nasce quem nunca entrou, e quem tem a posicao num mapa que deixou de existir. */
export const STORY_ENTRY_MAP_ID = 'town'
/** Alcance da conversa e do duelo com um NPC, em Chebyshev (docs/21 secoes 12 e 13). */
export const STORY_NPC_TALK_RANGE_TILES = 1
/** Ate onde o NPC se vira para o jogador, em Chebyshev (docs/21 secao 12). */
export const STORY_NPC_FACE_RANGE_TILES = 4

/** Os seis emotes (docs/21 secao 7). Lista fechada: emote e um id, nunca texto do jogador. */
export const STORY_EMOTES = ['wave', 'laugh', 'surprise', 'thumbs_up', 'angry', 'heart'] as const
export type StoryEmote = (typeof STORY_EMOTES)[number]

export const isStoryEmote = (value: unknown): value is StoryEmote =>
  typeof value === 'string' && (STORY_EMOTES as readonly string[]).includes(value)

/** As quatro direcoes de um passo (docs/21 secao 5). */
export const STORY_DIRECTIONS = ['up', 'down', 'left', 'right'] as const
export type StoryDirection = (typeof STORY_DIRECTIONS)[number]

/** O deslocamento de um passo em cada direcao, em tiles. `y` cresce para baixo, como no Tiled. */
export const STORY_STEP: Readonly<Record<StoryDirection, { dx: number; dy: number }>> = {
  up: { dx: 0, dy: -1 },
  down: { dx: 0, dy: 1 },
  left: { dx: -1, dy: 0 },
  right: { dx: 1, dy: 0 },
}

/**
 * Distancia de Chebyshev entre dois tiles: a diagonal conta como um (docs/21 secao 8). E a que decide o
 * alcance do desafio.
 */
export const tileDistance = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
  Math.max(Math.abs(a.x - b.x), Math.abs(a.y - b.y))

// ## O mapa (docs/21 secao 3)
//
// O SUBCONJUNTO do formato JSON do Tiled que o jogo le. O resto do arquivo -- propriedades do editor,
// camadas decorativas, `nextobjectid` -- passa sem ser olhado, por isso os objetos nao sao `strict`: o Tiled
// grava campos que nao interessam, e recusar o arquivo por isso seria recusar o editor.

const tileLayerSchema = z.object({
  type: z.literal('tilelayer'),
  name: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** gid por tile, linha a linha. 0 e vazio. */
  data: z.array(z.number().int().nonnegative()),
})

const propertySchema = z.object({ name: z.string(), type: z.string(), value: z.unknown() })

const objectLayerSchema = z.object({
  type: z.literal('objectgroup'),
  name: z.string(),
  objects: z.array(
    z.object({
      id: z.number().int(),
      name: z.string().optional(),
      x: z.number(),
      y: z.number(),
      width: z.number().optional(),
      height: z.number().optional(),
      properties: z.array(propertySchema).optional(),
    }),
  ),
})

/** O chao pintado de uma vez (docs/21 secao 3): cobre o mapa inteiro, a partir do canto. */
const imageLayerSchema = z.object({
  type: z.literal('imagelayer'),
  name: z.string(),
  /** Relativo ao arquivo do mapa: e o que o editor abre. O cliente acha a imagem pelo `mapId`. */
  image: z.string(),
})

const otherLayerSchema = z.object({ type: z.literal('group'), name: z.string() })

export const storyTilesetSchema = z.object({
  firstgid: z.number().int().positive(),
  name: z.string(),
  /** Relativo ao arquivo do mapa: e o que o editor abre. O cliente usa a versao WebP pelo `name`. */
  image: z.string(),
  tilewidth: z.number().int().positive(),
  tileheight: z.number().int().positive(),
  columns: z.number().int().positive(),
  tilecount: z.number().int().positive(),
})

export const storyMapSchema = z
  .object({
    orientation: z.literal('orthogonal'),
    infinite: z.literal(false),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    tilewidth: z.number().int().positive(),
    tileheight: z.number().int().positive(),
    properties: z.array(propertySchema).optional(),
    tilesets: z.array(storyTilesetSchema).min(1),
    layers: z.array(
      z.discriminatedUnion('type', [tileLayerSchema, objectLayerSchema, imageLayerSchema, otherLayerSchema]),
    ),
  })
  .superRefine((map, ctx) => {
    for (const problem of storyMapProblems(map)) ctx.addIssue({ code: 'custom', message: problem })
  })

export type StoryMap = z.infer<typeof storyMapSchema>
export type StoryTileLayer = z.infer<typeof tileLayerSchema>
export type StoryObjectLayer = z.infer<typeof objectLayerSchema>
export type StoryMapObject = StoryObjectLayer['objects'][number]

/** As tres camadas sem as quais o mapa nao e jogavel, com os tipos que cada uma aceita (docs/21 secao 3). */
export const STORY_REQUIRED_LAYERS = {
  ground: ['tilelayer', 'imagelayer'],
  collision: ['tilelayer'],
  spawn: ['objectgroup'],
} as const

type Property = { name: string; value?: unknown }
type MapObject = { x: number; y: number; name?: string | undefined; properties?: readonly Property[] | undefined }

type MapShape = {
  width: number
  height: number
  tilewidth: number
  tileheight: number
  layers: ReadonlyArray<
    | { type: 'tilelayer'; name: string; width: number; height: number; data: readonly number[] }
    | { type: 'objectgroup'; name: string; objects: readonly MapObject[] }
    | { type: 'imagelayer' | 'group'; name: string }
  >
}

/** O valor de uma propriedade do Tiled, se ela for texto. Serve ao mapa e aos objetos dele. */
export const stringProperty = (
  owner: { properties?: readonly Property[] | undefined },
  name: string,
): string | undefined => {
  const value = owner.properties?.find((property) => property.name === name)?.value
  return typeof value === 'string' ? value : undefined
}

/** O tile em que um objeto do Tiled esta, pelo canto superior esquerdo -- o ponto e o proprio canto. */
export const objectTile = (map: { tilewidth: number; tileheight: number }, object: { x: number; y: number }) => ({
  x: Math.floor(object.x / map.tilewidth),
  y: Math.floor(object.y / map.tileheight),
})

/**
 * O que impede um mapa de ser jogavel, em frases. Vazio e jogavel.
 *
 * Fica fora do schema para poder ser testada sozinha, e para o conteudo dizer QUAL mapa e por que, em vez de
 * um erro de zod sem contexto.
 */
export function storyMapProblems(map: MapShape): string[] {
  const problems: string[] = []
  const layerNamed = (name: string) => map.layers.find((layer) => layer.name === name)

  for (const [name, types] of Object.entries(STORY_REQUIRED_LAYERS)) {
    const layer = layerNamed(name)
    if (layer === undefined) problems.push(`falta a camada "${name}"`)
    else if (!(types as readonly string[]).includes(layer.type))
      problems.push(`a camada "${name}" deveria ser ${types.join(' ou ')}, e e ${layer.type}`)
  }
  if (map.tilewidth !== map.tileheight)
    problems.push(`o tile tem ${map.tilewidth}x${map.tileheight}, e deveria ser quadrado`)

  for (const layer of map.layers) {
    if (layer.type !== 'tilelayer') continue
    if (layer.width !== map.width || layer.height !== map.height)
      problems.push(`a camada "${layer.name}" tem ${layer.width}x${layer.height}, e o mapa ${map.width}x${map.height}`)
    if (layer.data.length !== map.width * map.height)
      problems.push(`a camada "${layer.name}" tem ${layer.data.length} tiles, e deveria ter ${map.width * map.height}`)
  }

  const collision = layerNamed('collision')
  /** Por que um tile nao serve a um objeto que precisa de chao livre, ou `undefined` se serve. */
  const unusable = (x: number, y: number): string | undefined => {
    if (x < 0 || y < 0 || x >= map.width || y >= map.height) return 'esta fora do mapa'
    if (collision?.type === 'tilelayer' && (collision.data[y * map.width + x] ?? 0) !== 0)
      return 'cai num tile bloqueado'
    return undefined
  }
  const objectsOf = (name: string): readonly MapObject[] | undefined => {
    const layer = layerNamed(name)
    return layer?.type === 'objectgroup' ? layer.objects : undefined
  }

  const spawn = objectsOf('spawn')
  if (spawn !== undefined) {
    if (spawn.length === 0) problems.push('a camada "spawn" nao tem nenhum ponto')
    for (const point of spawn) {
      const { x, y } = objectTile(map, point)
      const problem = unusable(x, y)
      if (problem !== undefined) problems.push(`o ponto de nascimento (${x}, ${y}) ${problem}`)
    }
  }

  // O portal e o NPC disputam o tile: pisar no portal e passar, e o NPC bloqueia o tile dele.
  const taken = new Map<number, string>()
  for (const portal of objectsOf('portals') ?? []) {
    const { x, y } = objectTile(map, portal)
    const problem = unusable(x, y)
    if (problem !== undefined) problems.push(`o portal em (${x}, ${y}) ${problem}`)
    if (stringProperty(portal, 'to') === undefined)
      problems.push(`o portal em (${x}, ${y}) nao diz para onde leva ("to")`)
    if (stringProperty(portal, 'spawn') === undefined)
      problems.push(`o portal em (${x}, ${y}) nao diz em que ponto se chega ("spawn")`)
    taken.set(y * map.width + x, 'um portal')
  }
  const npcIds = new Set<string>()
  for (const npc of objectsOf('npcs') ?? []) {
    const { x, y } = objectTile(map, npc)
    const id = npc.name ?? ''
    if (id === '') problems.push(`o NPC em (${x}, ${y}) nao tem nome`)
    else if (npcIds.has(id)) problems.push(`o NPC "${id}" aparece mais de uma vez`)
    npcIds.add(id)
    const neighbour = taken.get(y * map.width + x)
    const problem = unusable(x, y) ?? (neighbour === undefined ? undefined : `divide o tile com ${neighbour}`)
    if (problem !== undefined) problems.push(`o NPC "${id}" em (${x}, ${y}) ${problem}`)
    taken.set(y * map.width + x, `o NPC "${id}"`)
  }
  return problems
}

/** A grade de colisao que o cliente e o servidor consultam: `true` e bloqueado. */
export type CollisionGrid = {
  readonly width: number
  readonly height: number
  readonly blocked: readonly boolean[]
}

/** Fora do mapa conta como bloqueado: nao ha borda por onde sair. */
export const isBlocked = (grid: CollisionGrid, x: number, y: number): boolean =>
  x < 0 || y < 0 || x >= grid.width || y >= grid.height || grid.blocked[y * grid.width + x] === true

// ## Os NPCs (docs/21 secao 12)

/** Um NPC do mundo: conteudo, um arquivo por NPC, posto num tile pela camada `npcs` do mapa. */
export const storyNpcSchema = z
  .object({
    /** Estavel, e o mesmo nome do objeto na camada `npcs`. */
    id: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string().min(1),
    /** A direcao em que ele fica quando ninguem esta perto. */
    facing: z.enum(STORY_DIRECTIONS),
    /** As falas, em pt-BR. `greeting` e sorteada no cliente; `challenge`, `win` e `lose` so com `botId`. */
    lines: z
      .object({
        greeting: z.array(z.string().min(1)).min(1),
        challenge: z.string().min(1).optional(),
        win: z.string().min(1).optional(),
        lose: z.string().min(1).optional(),
      })
      .strict(),
    /** O bot do duelo contra ele (docs/21 secao 13), configurado no painel. Sem ele, o NPC so conversa. */
    botId: z.string().min(1).optional(),
    origin: z.enum(['own', 'third-party']),
  })
  .strict()
  .superRefine((npc, ctx) => {
    if (npc.botId !== undefined && npc.lines.challenge === undefined)
      ctx.addIssue({ code: 'custom', message: 'NPC que duela precisa da fala "challenge"' })
  })

export type StoryNpc = z.infer<typeof storyNpcSchema>
